import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAdminBooking } from '@/features/admin-bookings/hooks/useAdminBookings';
import {
  ATTENTION_DESCRIPTIONS,
  ATTENTION_LABELS,
  AUDIT_ACTION_LABELS,
  formatDate,
  shortBookingId,
} from '@/features/admin-bookings/constants';
import {
  BookingTimeline,
  type BookingTimelineEntry,
} from '@/features/admin-bookings/components/BookingTimeline';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { bookingStatusToBadge, formatStatusLabel } from '@/shared/utils/statusBadge';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { PRICING_TYPE_LABELS } from '@/features/booking/utils/bookingPricing';
import { BookingStatus } from '@/types';
import type { AdminBookingDetail } from '@/types';
import { formatBookingDuration, formatBookingWhen } from '@/shared/utils/bookingWhen';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ py: 2 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: '0.1em', display: 'block', mb: 1 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Field({ label, value, copy }: { label: string; value?: ReactNode; copy?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>
          {value ?? '—'}
        </Typography>
        {copy && (
          <Tooltip title="Copy">
            <IconButton
              size="small"
              aria-label={`Copy ${label}`}
              onClick={() => void navigator.clipboard?.writeText(copy)}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

const money = (amount: number | undefined, currency: string) =>
  amount == null ? undefined : formatGuidePrice(amount, currency);

const capitalize = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : undefined;

function buildTimeline(booking: AdminBookingDetail): BookingTimelineEntry[] {
  const history: BookingTimelineEntry[] = [
    {
      key: 'created',
      at: booking.createdAt,
      title: 'Booking created',
      actor: 'Traveler',
    },
    ...booking.statusHistory
      // The creation entry duplicates "Booking created".
      .filter((entry) => entry.from !== BookingStatus.CREATED)
      .map((entry, index) => ({
        key: `status-${index}`,
        at: entry.at,
        title: `${formatStatusLabel(entry.from)} → ${formatStatusLabel(entry.to)}`,
        actor: [capitalize(entry.actorRole), entry.actorName].filter(Boolean).join(' · ') || undefined,
        detail: entry.reason,
      })),
  ];

  const audit: BookingTimelineEntry[] = booking.auditTrail.map((log) => {
    const meta = log.metadata ?? {};
    const refunded = typeof meta.refundedAmount === 'number' ? meta.refundedAmount : undefined;
    return {
      key: `audit-${log.id}`,
      at: log.createdAt,
      title: AUDIT_ACTION_LABELS[log.action] ?? formatStatusLabel(log.action),
      actor: log.actorName ?? (log.actorId ? undefined : 'System'),
      detail: refunded != null ? `Amount ${money(refunded, booking.currency)}` : undefined,
    };
  });

  return [...history, ...audit].sort((a, b) => a.at.localeCompare(b.at));
}

export function AdminBookingDetailDrawer({
  bookingId,
  onClose,
}: {
  bookingId: string | null;
  onClose: () => void;
}) {
  const { data: booking, isLoading, isError } = useAdminBooking(bookingId);

  return (
    <Drawer
      anchor="right"
      open={Boolean(bookingId)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, bgcolor: 'background.paper' } }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Booking {bookingId ? shortBookingId(bookingId) : ''}
          </Typography>
          {booking && (
            <Typography variant="caption" color="text.secondary">
              Created {formatDate(booking.createdAt, true)}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label="Close booking details">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.5, pb: 4 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load this booking.
          </Alert>
        )}

        {booking && (
          <>
            {booking.attentionReasons.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {booking.attentionReasons.map((reason) => (
                  <Alert key={reason} severity="warning">
                    <strong>{ATTENTION_LABELS[reason]}:</strong> {ATTENTION_DESCRIPTIONS[reason]}
                  </Alert>
                ))}
              </Box>
            )}

            <Section title="Summary">
              <Field
                label="Status"
                value={
                  <StatusBadge
                    status={bookingStatusToBadge(booking.status)}
                    label={formatStatusLabel(booking.status)}
                  />
                }
              />
              <Field label="When" value={formatBookingWhen(booking)} />
              <Field label="Destination" value={booking.destinationLabel ?? booking.destinationName} />
              <Field
                label="Package"
                value={`${PRICING_TYPE_LABELS[booking.pricingType] ?? booking.pricingType} · ${formatBookingDuration(booking)} · ${booking.travelerCount} traveler${booking.travelerCount === 1 ? '' : 's'}`}
              />
              <Field label="Amount" value={money(booking.grossAmount, booking.currency)} />
              <Field label="Booking id" value={booking.id} copy={booking.id} />
              {booking.notes && <Field label="Traveler notes" value={booking.notes} />}
            </Section>
            <Divider />

            <Section title="Traveler">
              <Field label="Name" value={booking.traveler.contactSnapshot?.name ?? booking.traveler.name} />
              <Field
                label="Contact phone"
                value={booking.traveler.contactSnapshot?.phone ?? booking.traveler.contactPhone}
              />
              <Field label="Account phone" value={booking.traveler.phone} />
              <Field label="Email" value={booking.traveler.email} />
            </Section>
            <Divider />

            <Section title="Guide">
              <Field label="Name" value={booking.guide.fullName} />
              <Field label="Phone" value={booking.guide.phone} />
              <Field label="WhatsApp" value={booking.guide.whatsappNumber} />
              <Field label="Email" value={booking.guide.email} />
              {booking.guide.profileId && (
                <Button
                  component={RouterLink}
                  to={ADMIN_ROUTES.review(booking.guide.profileId)}
                  size="small"
                  sx={{ mt: 1, px: 0 }}
                >
                  Open guide profile
                </Button>
              )}
            </Section>
            <Divider />

            <Section title="Payments & refunds">
              {booking.payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payment attempts yet.
                </Typography>
              ) : (
                booking.payments.map((payment, index) => (
                  <Box
                    key={payment.id}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <Field
                      label={
                        payment.purpose === 'trip_extension'
                          ? `Extra time · attempt ${index + 1}`
                          : `Attempt ${index + 1}`
                      }
                      value={`${payment.status} · ${money(payment.amount, payment.currency)}`}
                    />
                    <Field label="Provider" value={capitalize(payment.provider)} />
                    <Field label="Order id" value={payment.providerOrderId} copy={payment.providerOrderId} />
                    {payment.providerPaymentId && (
                      <Field
                        label="Payment id"
                        value={payment.providerPaymentId}
                        copy={payment.providerPaymentId}
                      />
                    )}
                    <Field label="Started" value={formatDate(payment.createdAt, true)} />
                    {payment.lastFailedAttemptAt && (
                      <Field label="Last failed try" value={formatDate(payment.lastFailedAttemptAt, true)} />
                    )}
                  </Box>
                ))
              )}

              {booking.refunds.map((refund) => (
                <Box
                  key={refund.id}
                  sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <Field label="Refund" value={money(refund.amount, refund.currency)} />
                  <Field label="Status" value="Submitted to provider" />
                  <Field label="Refund id" value={refund.id} copy={refund.id} />
                  <Field label="Reason" value={refund.reason} />
                  <Field label="Submitted" value={formatDate(refund.createdAt, true)} />
                </Box>
              ))}
            </Section>
            <Divider />

            <Section title="Money split">
              {booking.settlement ? (
                <>
                  <Field label="Gross" value={money(booking.settlement.grossAmount, booking.settlement.currency)} />
                  <Field
                    label="Guide share"
                    value={money(booking.settlement.guideAmount, booking.settlement.currency)}
                  />
                  <Field
                    label="Platform commission"
                    value={money(booking.settlement.platformAmount, booking.settlement.currency)}
                  />
                  {booking.settlement.reversedGuideAmount != null && (
                    <Field
                      label="Reversed from guide"
                      value={money(booking.settlement.reversedGuideAmount, booking.settlement.currency)}
                    />
                  )}
                  <Field label="Settlement status" value={formatStatusLabel(booking.settlement.status)} />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No settlement yet (created when payment is confirmed).
                </Typography>
              )}
              {(booking.extensionSettlements ?? []).map((settlement) => (
                <Box
                  key={settlement.id}
                  sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <Field label="Extra time gross" value={money(settlement.grossAmount, settlement.currency)} />
                  <Field label="Guide share" value={money(settlement.guideAmount, settlement.currency)} />
                  <Field
                    label="Platform commission"
                    value={money(settlement.platformAmount, settlement.currency)}
                  />
                  {settlement.reversedGuideAmount != null && (
                    <Field
                      label="Reversed from guide"
                      value={money(settlement.reversedGuideAmount, settlement.currency)}
                    />
                  )}
                  <Field label="Settlement status" value={formatStatusLabel(settlement.status)} />
                </Box>
              ))}
            </Section>

            {(booking.extensions ?? []).length > 0 && (
              <>
                <Divider />
                <Section title="Extra time">
                  {booking.extensions!.map((ext) => (
                    <Box
                      key={ext.id}
                      sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                    >
                      <Field
                        label={`+${ext.hours} h (${ext.requestedBy === 'guide' ? 'guide suggested' : 'traveler requested'})`}
                        value={`${formatStatusLabel(ext.status)} · ${money(ext.totalAmount, ext.currency)}`}
                      />
                      <Field
                        label="Window"
                        value={`${formatDate(ext.fromEndAt, true)} → ${formatDate(ext.toEndAt, true)}`}
                      />
                      <Field label="Requested" value={formatDate(ext.createdAt, true)} />
                      <Field label="Hold until" value={formatDate(ext.expiresAt, true)} />
                      {ext.paidAt && <Field label="Paid" value={formatDate(ext.paidAt, true)} />}
                    </Box>
                  ))}
                </Section>
              </>
            )}

            {(booking.cancelledAt || booking.cancellationQuote) && (
              <>
                <Divider />
                <Section title="Cancellation">
                  {booking.cancelledAt ? (
                    <>
                      <Field label="Cancelled" value={formatDate(booking.cancelledAt, true)} />
                      <Field label="Reason" value={booking.cancelReason} />
                      <Field label="Policy tier" value={booking.cancelPolicyTier} />
                      <Field
                        label="Refund"
                        value={
                          booking.cancelRefundAmount != null
                            ? `${money(booking.cancelRefundAmount, booking.currency)} (${booking.cancelRefundPercent ?? 0}%)`
                            : undefined
                        }
                      />
                      <Field label="Policy version" value={booking.cancelPolicyVersion} />
                    </>
                  ) : (
                    booking.cancellationQuote && (
                      <Typography variant="body2">
                        If the traveler cancelled now:{' '}
                        <strong>
                          {booking.cancellationQuote.allowed
                            ? `${money(booking.cancellationQuote.refundAmount, booking.currency)} refund (${booking.cancellationQuote.refundPercent}%)`
                            : 'not allowed'}
                        </strong>
                        . {booking.cancellationQuote.message}
                      </Typography>
                    )
                  )}
                </Section>
              </>
            )}

            {booking.disputedAt && (
              <>
                <Divider />
                <Section title="Dispute">
                  <Field label="Raised" value={formatDate(booking.disputedAt, true)} />
                  <Field label="Reason" value={booking.disputeReason} />
                  <Field label="Resolution" value={booking.disputeResolution ? formatStatusLabel(booking.disputeResolution) : 'Open'} />
                  {booking.disputeResolvedAt && (
                    <Field
                      label="Resolved"
                      value={`${formatDate(booking.disputeResolvedAt, true)}${booking.disputeResolvedByName ? ` by ${booking.disputeResolvedByName}` : ''}`}
                    />
                  )}
                  {booking.status === BookingStatus.DISPUTED && (
                    <Button
                      component={RouterLink}
                      to={`${ADMIN_ROUTES.FINANCE}?tab=disputes`}
                      variant="contained"
                      size="small"
                      sx={{ mt: 1.5 }}
                    >
                      Resolve in Finance → Disputes
                    </Button>
                  )}
                </Section>
              </>
            )}
            <Divider />

            <Section title="Timeline">
              <BookingTimeline entries={buildTimeline(booking)} />
            </Section>
          </>
        )}
      </Box>
    </Drawer>
  );
}
